import { defineModule, f } from "../../contract";
import { DNA_TESTS, EMBRYO_GRADES, EMBRYO_STAGES, EVENTS } from "./contract/events";
import { geneticsFacet } from "./contract/facet";
import { FREEZE_LABELS, LABEL_MESSAGES, RIGHTS_LABELS, SEXED_LABELS } from "./shared/labels";
import { TRAIT_MESSAGES } from "./shared/traits";

const location = {
  tank: f.text("Semen tank"),
  canister: f.text("Canister"),
  cane: f.text("Cane"),
};
const ownership = {
  owner_id: f.ref("Owner", "contact", { level: "more", help: "Leave empty when the ranch owns it." }),
  rights: f.enum("Use rights", RIGHTS_LABELS as Record<"full" | "use_only" | "restricted", string>, { level: "more" }),
  restrictions: f.text("Restrictions", { level: "more" }),
};

export default defineModule({
  id: "genetics",
  label: "Genetics",
  description: "Pedigree, EPDs, DNA, registration, mating plans and genetic inventory.",
  events: EVENTS,
  facets: [geneticsFacet],
  jobs: [
    {
      type: "genetics.registration",
      label: "Registration batch",
      description: "Calves going to one association together, from the file to registration numbers back.",
      icon: "award",
      tone: "genetics",
      workspace: "registration",
      subjectKind: "animal",
      fields: {
        association: f.text("Association", { required: true }),
        season: f.text("Season"),
        membership: f.text("Member number", { level: "more" }),
        require_dna: f.bool("DNA parent check required", { level: "more" }),
      },
      stages: [
        { id: "prepared", label: "Preparing" }, { id: "exported", label: "File made" }, { id: "submitted", label: "Sent" },
        { id: "accepted", label: "Accepted" }, { id: "partial", label: "Partly accepted" }, { id: "rejected", label: "Rejected" },
      ],
      agent: { when: "Start registering a set of calves with an association, e.g. 'register the spring Red Angus calves'." },
    },
    {
      type: "genetics.dna_order",
      label: "DNA order",
      description: "A batch of DNA samples from pulling them to results back.",
      icon: "dna",
      tone: "genetics",
      workspace: "genetics",
      subjectKind: "animal",
      itemEvent: "genetics.dna_sample",
      fields: {
        lab: f.text("Lab"),
        tests: f.multiEnum("Tests", DNA_TESTS),
        order_number: f.text("Order number", { level: "more" }),
      },
      stages: [{ id: "collecting", label: "Pulling samples" }, { id: "sent", label: "At the lab" }, { id: "results", label: "Results back" }],
      agent: { when: "A round of DNA sampling for a set of animals, e.g. 'we need to pull hair on the 2026 heifer calves'." },
    },
    {
      type: "genetics.mating_plan",
      label: "Mating plan",
      description: "Which bull or semen goes on which female. A plan, not a breeding, until the AI or exposure is recorded.",
      icon: "mating",
      tone: "genetics",
      workspace: "genetics",
      subjectKind: "animal",
      fields: {
        season: f.text("Season"),
        association: f.text("EPDs from", { level: "more" }),
      },
      stages: [{ id: "planning", label: "Planning" }, { id: "ready", label: "Ready to breed" }, { id: "breeding", label: "Breeding" }],
      agent: { when: "Plan which bulls or semen to use on a set of females." },
    },
  ],
  workspaces: [
    {
      id: "genetics",
      label: "Genetics and mating",
      description: "Pedigrees, EPD comparisons, DNA orders and results, mating plans and trait definitions.",
      icon: "dna",
      group: "breed",
      keywords: ["pedigree", "family tree", "epd", "ebv", "dna", "genomic", "parentage", "mating", "sire selection", "bull selection", "inbreeding", "trait", "contemporary group", "breed makeup", "hair sample"],
      keywordsEs: ["pedigrí", "genealogía", "árbol", "epd", "adn", "genómica", "paternidad", "apareamiento", "toro", "consanguinidad", "grupo contemporáneo", "muestra de pelo", "raza"],
      presets: ["seedstock"],
    },
    {
      id: "registration",
      label: "Register calves",
      description: "Registration batches, readiness, files, returned numbers and rejections.",
      icon: "award",
      group: "breed",
      keywords: ["register", "registration", "association", "papers", "registry", "transfer", "red angus", "angus", "hereford", "simmental", "registration numbers"],
      keywordsEs: ["registrar", "registro", "asociación", "papeles", "transferencia", "número de registro"],
      presets: ["seedstock"],
    },
  ],
  ranchSections: [
    {
      id: "genetic-inventory",
      label: "Semen and embryos",
      description: "Straws and embryos by tank and sire, what's reserved, and every use.",
      icon: "tank",
      group: "supplies",
      keywords: ["semen", "straws", "tank", "embryos", "canister", "cane", "flush", "ai sires", "collection"],
      keywordsEs: ["semen", "pajillas", "termo", "embriones", "canastilla", "lavado", "colecta"],
    },
  ],
  stockKinds: [
    {
      kind: "semen",
      label: "Semen",
      fields: {
        sire_id: f.ref("Sire", "animal"),
        registration: f.text("Sire registration"),
        breed: f.text("Breed", { level: "more" }),
        supplier: f.text("Stud or supplier", { level: "more" }),
      },
      lotFields: {
        collection_date: f.date("Collected"),
        ...location,
        sexed: f.enum("Sorted", SEXED_LABELS as Record<"conventional" | "sexed_female" | "sexed_male" | "unknown", string>),
        origin: f.enum("Came from", { purchased: "Bought", collected: "Collected here", opening: "Opening balance", other: "Other" }, { level: "more" }),
        purchased_from: f.ref("Bought from", "contact", { level: "more" }),
        cost: f.money("Cost per straw", { level: "more" }),
        ...ownership,
      },
    },
    {
      kind: "embryo",
      label: "Embryos",
      fields: {
        donor_id: f.ref("Donor", "animal"),
        sire_id: f.ref("Sire", "animal"),
        breed: f.text("Breed", { level: "more" }),
      },
      lotFields: {
        flush_date: f.date("Flushed"),
        grade: f.enum("Grade", EMBRYO_GRADES),
        stage: f.enum("Stage", EMBRYO_STAGES),
        freeze: f.enum("Freezing", FREEZE_LABELS as Record<"direct_transfer" | "glycerol" | "vitrified" | "fresh", string>),
        ...location,
        sexed: f.enum("Sorted", SEXED_LABELS as Record<"conventional" | "sexed_female" | "sexed_male" | "unknown", string>, { level: "more" }),
        numbers: f.text("Embryo numbers", { level: "more", placeholder: "01-06" }),
        cost: f.money("Cost per embryo", { level: "more" }),
        ...ownership,
      },
    },
  ],
  settings: {
    association: f.text("Main association", { help: "Used to start registration batches and EPD comparisons." }),
    membership: f.text("Member number"),
    herd_prefix: f.text("Herd prefix", { level: "more" }),
  },
  messages: [...TRAIT_MESSAGES, ...LABEL_MESSAGES],
});

export type { GeneticsState } from "./shared/types";
